import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { FORM_NAMESPACE, FORM_TYPES } from "SharedStores/Form/formStore";
import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
import { FormulaEvaluator } from "./evaluator";
import { convertValueToString, areCalculatedDecimalValuesEqual } from "Util/util";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";

function areCalculatedResultsEqual(evaluatedFormula, storedValueStr) {
    // Decimal-valued formula: Do a fuzzy compare on the values
    if (evaluatedFormula.ResultType === DATA_FIELD_TYPE.Decimal) {
        return areCalculatedDecimalValuesEqual(storedValueStr, evaluatedFormula.result.Value);
    } else {
        // Non-Decimal formula: just compare string value with stored string
        const evaluatedResultStr = convertValueToString(evaluatedFormula.result.Value);
        return evaluatedResultStr === storedValueStr;
    }
}

export default {
    data() {
        return {
            calculatedFieldController_unwatchHooks: []
        };
    },
    methods: {
        calculatedFieldController_init() {
            if (!isModuleRegistered(PROGRAM_NAMESPACE, this.$store.state)) {
                throw Error("Program Module not registered.");
            }
            if (!isModuleRegistered(PROJECT_NAMESPACE, this.$store.state)) {
                throw Error("Project Module not registered.");
            }

            let calculatedFields = [];
            // if we have a form, only watch those calculated fields that are in the current form.
            const test = true;
            if (test && isModuleRegistered(FORM_NAMESPACE, this.$store.state)) {
                const elements = this.$store.getters[FORM_TYPES.getters.orderedFormElements];
                calculatedFields = elements
                    .filter((e) => e.ClassName === "FormulaResultElement")
                    .map((e) =>
                        this.$store.getters[PROGRAM_TYPES.getters.calculatedFieldFromId](e.Properties.formulaid)
                    );
            } else {
                for (const calculatedFieldId in this.$store.state[PROGRAM_NAMESPACE].CalculatedFields) {
                    if (this.$store.state[PROGRAM_NAMESPACE].CalculatedFields.hasOwnProperty(calculatedFieldId)) {
                        const calculatedField =
                            this.$store.state[PROGRAM_NAMESPACE].CalculatedFields[calculatedFieldId];
                        calculatedFields.push(calculatedField);
                    }
                }
            }

            /**
             * We need to calculate a value that changes based on the calculated field dependencies,
             * but we need to make sure that value can be grabbed in a synchronous fashion.
             * That is why we call evaluator.getReferencedValuesDictionary() which is sync, instead of
             * evaluator.eval(), which is async. Then in the watcher hook, we can do the async evaluation
             * and properly store the value in the store when we have it.
             */
            calculatedFields.forEach((calculatedField) => {
                //prettier-ignore
                const unwatchFn = this.$store.watch(
                    (state, getters) => {
                        if (!calculatedField) {
                            return null;
                        }
                        const clone = JSON.parse(JSON.stringify(calculatedField));
                        const evaluator = new FormulaEvaluator(this.$app,
                            clone,
                            { rootState: state, rootGetters: getters },
                            this.$serviceControllers);

                        const cloneWithReferencedValues = {
                            referencedValues: evaluator.getReferencedValuesDictionary(),
                            clonedCalcField: clone
                        };

                        //The object returned here aren't actually referenced/used anywhere
                        //they are basically a side-effect of putting a watch on the objects/values within them
                        //which allows the calculated field summary on the form element to stay updated 

                        return cloneWithReferencedValues;
                    },
                    this.calculatedFieldController_watcherCallback,
                    // immediate: evaluate the watcher right away and trigger the callback
                    // deep: watch changes to multi-instance data fields
                    { immediate: true, deep: true }
                );
                this.calculatedFieldController_unwatchHooks.push(unwatchFn);
            });
        },
        async calculatedFieldController_watcherCallback(cloneWithReferencedValues) {
            if (!cloneWithReferencedValues || !cloneWithReferencedValues.clonedCalcField) {
                return;
            }
            const evaluator = new FormulaEvaluator(
                this.$app,
                cloneWithReferencedValues.clonedCalcField,
                { rootState: this.$store.state, rootGetters: this.$store.getters },
                this.$serviceControllers
            );
            await evaluator.eval();
            const evaluatedFormula = cloneWithReferencedValues.clonedCalcField;

            const dataFieldId = evaluatedFormula.PublicDataFieldDefinitionId;
            this.$store.commit(
                PROJECT_TYPES.mutations.commitEvaluatedField,
                { evaluatedField: evaluatedFormula },
                { root: true }
            );
            const currentValue = this.$store.getters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);
            if (!areCalculatedResultsEqual(evaluatedFormula, currentValue)) {
                const value = convertValueToString(evaluatedFormula.result.Value);
                if (isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state)) {
                    this.$store.dispatch(
                        PROJECT_FORM_TYPES.actions.setProjectData,
                        { dataFieldId, value },
                        { root: true }
                    );
                } else {
                    this.$store.commit(
                        PROJECT_TYPES.mutations.commitProjectData,
                        { dataFieldId, value },
                        { root: true }
                    );
                }
            }
        },
        calculatedFieldController_unhook() {
            this.calculatedFieldController_unwatchHooks.forEach((unwatchHook) => unwatchHook());
        }
    }
};
